import dayjs from "dayjs";
import { localDateRangeToUtc } from "app/utils/timezone";

export type AssignmentFilter = "unassigned" | "assigned" | "all";

export type OrdersBoardStage = {
  id: number | string;
  title: string;
  color?: string;
  items: any[];
};

export const buildPickingFilters = (options: {
  startDate: dayjs.Dayjs;
  endDate?: dayjs.Dayjs;
  timezone: string;
}) => {
  const endDate = options.endDate ?? options.startDate;
  const startRange = localDateRangeToUtc(options.startDate, options.timezone);
  const endRange = localDateRangeToUtc(endDate, options.timezone);

  return [
    ["company_id", "=", 1],
    ["state", "=", "done"],
    ["scheduled_date", ">=", startRange.startUtc],
    ["scheduled_date", "<=", endRange.endUtc],
    ["delivery_area", "!=", false],
  ];
};

export const getOdooRelationId = (value: unknown): number | null => {
  if (value == null || value === false) {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === "number") {
    return value[0];
  }
  if (typeof value === "object" && typeof (value as { id?: unknown }).id === "number") {
    return (value as { id: number }).id;
  }
  return null;
};

export const buildPickingOrdersTableData = (
  areas: any[],
  pickingOrdersValue: any[]
): OrdersBoardStage[] => {
  const safeAreas = Array.isArray(areas) ? areas : [];
  const orders = Array.isArray(pickingOrdersValue) ? pickingOrdersValue : [];
  const ordersByArea = new Map<number | string, any[]>();

  for (const order of orders) {
    const areaId = getOdooRelationId(order?.delivery_area) ?? "none";
    const bucket = ordersByArea.get(areaId);
    if (bucket) {
      bucket.push(order);
    } else {
      ordersByArea.set(areaId, [order]);
    }
  }

  const data = safeAreas.map((dItem: any) => ({
    title: dItem?.name,
    items: ordersByArea.get(dItem?.id) ?? [],
    color: dItem?.color,
    id: dItem?.id,
  }));

  data.push({
    title: "None",
    items: ordersByArea.get("none") ?? [],
    color: "#808080",
    id: "none",
  });

  return data;
};

export const isPickingAssigned = (item: any): boolean => {
  const driver = item?.driver_id;
  if (!driver || driver === false) {
    return false;
  }
  if (typeof driver === "object") {
    return Boolean(driver.id);
  }
  return Boolean(driver);
};

export const filterPickingOrders = (
  orders: any[],
  options: {
    search: string;
    assignmentFilter: AssignmentFilter;
  }
): any[] => {
  let filtered = Array.isArray(orders) ? orders : [];
  const query = options.search.trim().toLowerCase();

  if (query) {
    filtered = filtered.filter((item: any) =>
      item?.name?.toLowerCase().includes(query)
    );
  }

  if (options.assignmentFilter === "unassigned") {
    filtered = filtered.filter((item: any) => !isPickingAssigned(item));
  } else if (options.assignmentFilter === "assigned") {
    filtered = filtered.filter((item: any) => isPickingAssigned(item));
  }

  return filtered;
};
